/*
 * /people/[personId] (page)
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import {
	Badge,
	Button,
	ConfirmDialog,
	ConfirmDialogAction,
	ConfirmDialogCancel,
	ConfirmDialogContent,
	ConfirmDialogDescription,
	ConfirmDialogFooter,
	ConfirmDialogHeader,
	ConfirmDialogTitle,
	ConfirmDialogTrigger,
	Container,
	Divider,
	Page,
	PageHeader,
	Paper,
	Skeleton,
} from "@psychology/react-ui"
import { ArrowLeft, Pencil, Trash2, User } from "lucide-react"
import { PersonForm } from "../../features/people/components/person-form"
import { trpc } from "../../trpc"
import { authPageLoader } from "../../utils/auth.utils"

export const Route = createFileRoute("/people/$personId")({
	component: PersonDetailPage,
	loader: authPageLoader,
})

function PersonDetailPage() {
	const { personId } = Route.useParams()
	const { user } = Route.useLoaderData()
	const navigate = useNavigate()
	const utils = trpc.useUtils()

	const {
		data: person,
		isLoading,
		refetch,
	} = trpc.person.getByFilter.useQuery({
		where: { id: personId },
	})

	const deleteMutation = trpc.person.delete.useMutation({
		onSuccess: async () => {
			await utils.person.getAllByFilter.invalidate()
			navigate({ to: "/people" })
		},
	})

	if (isLoading) {
		return (
			<Page centerVertical={false}>
				<Container width={"xl"} padding={"lg"} gap={"sm"}>
					<Skeleton className={"h-8 w-48"} />
					<Divider />
					<div className={"flex flex-col gap-4"}>
						<Skeleton className={"h-24 w-full"} />
						<Skeleton className={"h-20 w-full"} />
					</div>
				</Container>
			</Page>
		)
	}

	if (!person) {
		return (
			<Page centerVertical={false}>
				<Container width={"xl"} padding={"lg"} gap={"sm"}>
					<PageHeader
						title={"Person not found"}
						left={{
							href: "/people",
							label: "People",
							Icon: <ArrowLeft size={20} />,
						}}
					/>
					<Divider />
					<p className={"text-muted-foreground text-sm"}>
						{"This person could not be found."}
					</p>
				</Container>
			</Page>
		)
	}

	const displayLabel = person.displayName || person.name
	const nicknameList = (person.nicknames as string[] | null) ?? []

	return (
		<Page centerVertical={false}>
			<Container width={"xl"} padding={"lg"} gap={"sm"}>
				<PageHeader
					title={displayLabel}
					left={{
						href: "/people",
						label: "People",
						Icon: <ArrowLeft size={20} />,
					}}
					right={{
						Component: (
							<div className={"flex items-center justify-end gap-2"}>
								<PersonForm
									mode={"edit"}
									userId={user.id}
									person={person}
									onSuccess={() => refetch()}
									trigger={
										<Button size={"sm"} variant={"outline"}>
											<Pencil size={14} className={"mr-1.5"} />
											{"Edit"}
										</Button>
									}
								/>
								<ConfirmDialog>
									<ConfirmDialogTrigger asChild>
										<Button
											size={"sm"}
											variant={"outline"}
											className={"text-destructive hover:text-destructive"}
										>
											<Trash2 size={14} className={"mr-1.5"} />
											{"Remove"}
										</Button>
									</ConfirmDialogTrigger>
									<ConfirmDialogContent>
										<ConfirmDialogHeader>
											<ConfirmDialogTitle>
												{"Remove person?"}
											</ConfirmDialogTitle>
											<ConfirmDialogDescription>
												{`This will permanently remove "${displayLabel}" and all their information. This cannot be undone.`}
											</ConfirmDialogDescription>
										</ConfirmDialogHeader>
										<ConfirmDialogFooter>
											<ConfirmDialogCancel>{"Cancel"}</ConfirmDialogCancel>
											<ConfirmDialogAction
												onClick={() => deleteMutation.mutate({ id: person.id })}
											>
												{"Remove"}
											</ConfirmDialogAction>
										</ConfirmDialogFooter>
									</ConfirmDialogContent>
								</ConfirmDialog>
							</div>
						),
					}}
				/>
				<Divider />

				<div className={"flex flex-col gap-4"}>
					<Paper className={"flex flex-col gap-4 p-5"}>
						<div className={"flex items-center gap-3"}>
							<div
								className={
									"bg-muted flex h-12 w-12 shrink-0 items-center justify-center rounded-full"
								}
							>
								<User size={22} className={"text-muted-foreground"} />
							</div>
							<div className={"flex flex-col gap-1"}>
								<div className={"flex flex-wrap items-center gap-2"}>
									<span className={"text-lg font-semibold"}>
										{displayLabel}
									</span>
									{person.relationship && (
										<Badge variant={"secondary"} className={"capitalize"}>
											{person.relationship}
										</Badge>
									)}
								</div>
								{nicknameList.length > 0 && (
									<p className={"text-muted-foreground text-sm"}>
										{`Also known as: ${nicknameList.join(", ")}`}
									</p>
								)}
							</div>
						</div>
					</Paper>

					{person.notes && (
						<Paper className={"flex flex-col gap-3 p-5"}>
							<h3
								className={
									"text-muted-foreground text-xs font-semibold uppercase tracking-wide"
								}
							>
								{"Notes"}
							</h3>
							<p className={"text-sm whitespace-pre-wrap"}>{person.notes}</p>
						</Paper>
					)}
				</div>
			</Container>
		</Page>
	)
}
